import styled, { css } from "styled-components";

const theme = {
  bg: "#1C1E2A",
  surface: "#252836",
  surfaceHover: "#2d3040",
  primary: "#50fa7b",
  secondary: "#8be9fd",
  accent: "#ff79c6",
  text: "#f8f8f2",
  textSecondary: "#bd93f9",
  border: "#44475a",
  danger: "#ff5555",
  warning: "#f1fa8c",
};

export const TabelaContainer = styled.div`
  background-color: ${theme.surface};
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);

  /* MUDANÇA PRINCIPAL: Permite scroll horizontal na tabela */
  overflow-x: auto;

  /* Estilizando a barra de rolagem pra ficar bonita */
  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${theme.bg};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${theme.accent};
    border-radius: 10px;
    border: 2px solid ${theme.bg};
  }
`;

export const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: ${theme.text};

  /* ADICIONADO: Garante que a tabela tenha uma largura mínima. */
  /* Se a tela for menor que isso, a barra de rolagem aparece. */
  min-width: 800px;
`;

export const TabelaHead = styled.thead`
  background-color: rgba(0, 0, 0, 0.2);
`;

export const TabelaHeader = styled.th`
  padding: 1rem 1.5rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.8rem;
  color: ${theme.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${theme.border};

  /* ADICIONADO: Impede que os títulos quebrem linha */
  white-space: nowrap;
`;

export const TabelaBody = styled.tbody``;

export const TabelaRow = styled.tr`
  border-bottom: 1px solid ${theme.border};
  transition: all 0.2s ease-in-out;
  position: relative;

  &:last-child {
    border-bottom: none;
  }

  /* Estilização para Golden Box */
  ${(props) =>
    props.isGolden &&
    css`
      background-color: rgba(241, 250, 140, 0.08); /* Dourado bem suave */
      border-left: 4px solid #f1fa8c; /* Borda lateral dourada/amarela */

      /* Opcional: faz o texto brilhar um pouco */
      td {
        color: #f1fa8c;
        text-shadow: 0 0 5px rgba(241, 250, 140, 0.2);
      }
    `}

  ${(props) =>
    props.isClickable &&
    css`
      cursor: pointer;
      &:hover {
        /* Se for Golden, o hover fica um dourado mais forte, se não, usa o padrão */
        background-color: ${props.isGolden
          ? "rgba(241, 250, 140, 0.15)"
          : theme.surfaceHover};
      }
    `}
`;

export const TabelaData = styled.td`
  padding: 1rem 1.5rem;
  text-align: left;
  vertical-align: middle;
  font-size: 0.9rem;

  /* ADICIONADO: Impede que o conteúdo das células quebre de forma estranha */
  white-space: nowrap;
`;

const getStatusColor = (status, theme) => {
  switch (status) {
    case 1:
      return theme.warning;
    case 2:
      return theme.primary;
    case 3:
      return theme.danger;
    default:
      return theme.border;
  }
};

export const StatusBadge = styled.span`
  padding: 0.3rem 0.8rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #1c1e2a;
  background-color: ${({ status }) => getStatusColor(status, theme)};
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  gap: 0.5rem;
  background-color: ${theme.surface};
  border-top: 1px solid ${theme.border};

  /* ADICIONADO: Permite que a paginação quebre a linha em telas muito pequenas */
  flex-wrap: wrap;
`;

export const PaginationButton = styled.button`
  background-color: ${theme.border};
  color: ${theme.text};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  height: 40px;
  width: 40px;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${theme.accent};
    color: white;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const PageInfo = styled.span`
  color: ${theme.textSecondary};
  font-size: 0.9rem;
  margin: 0 1rem;
`;

export const PageInput = styled.input`
  height: 40px;
  width: 120px;
  background-color: ${theme.bg};
  border: 1px solid ${theme.border};
  border-radius: 8px;
  color: ${theme.text};
  text-align: center;
  font-size: 0.9rem;
  margin: 0 0.5rem;

  &:focus {
    outline: none;
    border-color: ${theme.accent};
  }
`;

export const Filters = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;

  /* MUDANÇA PRINCIPAL: Faz os filtros se empilharem no mobile */
  flex-wrap: wrap;
`;

export const FilterBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  /* ADICIONADO: Faz cada filtro ocupar o espaço disponível */
  flex-grow: 1;

  .name {
    font-size: 0.8rem;
    font-weight: 600;
    color: ${theme.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .input-box {
    width: 100%;
    min-width: 200px; /* Garante uma largura mínima mesmo quando empilhado */
    height: 42px;
    background-color: ${theme.surface};
    border: 1px solid ${theme.border};
    border-radius: 8px;
    color: ${theme.text};
    font-size: 0.9rem;
    padding: 0 1rem;
    box-sizing: border-box;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: ${theme.accent};
    }

    &[as="select"] {
      cursor: pointer;
    }
  }
`;
